import BankAccountOnFile from '@liveauctioneers/caterwaul-components/lib/BankAccountOnFile/BankAccountOnFile';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Caption } from '@liveauctioneers/hammer-ui-core/text';
import React, { useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';

import BankAccountStatus from '@/enums/bankAccountStatus';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { addPlaidBankAccount } from '@/redux/modules/account/payments/bankAccount/bankAccount.actions';
import { getPlaidLinkToken } from '@/redux/modules/account/payments/bankAccount/bankAccount.selectors';
import { getBidderId, getUserFirstName, getUserLastName } from '@/redux/modules/account/user/user.selectors';

type Props = React.ComponentProps<typeof BankAccountOnFile>;

const onExit = (error, metadata) => {
    // log errors if any
    if (error) {
        console.error(error, metadata);
    }
};

/**
 * Do not use this component directly, use the container.
 * This component has a container because we cannot call the usePlaid link unless we have a token, or it will fail.
 */

const BankAccountOnFileWithPlaidVerify = ({ bankAccount, ...rest }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const lastName = useAppSelector((state) => getUserLastName(state));
    const firstName = useAppSelector((state) => getUserFirstName(state));
    const bidderId = useAppSelector((state) => getBidderId(state));
    const { plaidToken: accessToken } = bankAccount;

    const plaidLinkToken = useAppSelector((state) => getPlaidLinkToken(state, accessToken));

    const onSuccess = useCallback(
        (token, metadata) => {
            dispatch(
                addPlaidBankAccount({
                    accessToken,
                    accountId: metadata.account_id,
                    bidderId,
                    firstName,
                    lastName,
                    token,
                })
            );
        },
        [accessToken, bidderId, dispatch, firstName, lastName]
    );

    const config = {
        onExit,
        onSuccess,
        token: plaidLinkToken,
    };

    const { open } = usePlaidLink(config);

    const pendingAuto = bankAccount.status === BankAccountStatus.PendingAutomatic;
    const pendingManual = bankAccount.status === BankAccountStatus.PendingManual;
    const pending = pendingAuto || pendingManual;

    const verifyText = pendingManual ? formatMessage({ id: 'bankAccount.verify' }) : '';

    const pendingText = pending ? formatMessage({ id: 'bankAccount.pendingVerification' }) : '';

    return (
        <div>
            <Caption
                className="mb-[10px] uppercase"
                color="critical"
            >
                {pendingText}
            </Caption>
            <BankAccountOnFile
                bankAccount={bankAccount}
                {...rest}
                verifyAccount={
                    pendingManual
                        ? (event) => {
                              event.stopPropagation();
                              open();
                          }
                        : undefined
                }
                verifyAccountLinkText={verifyText}
            />
        </div>
    );
};

export default BankAccountOnFileWithPlaidVerify;
